import React from "react";
import { ListTableColumn, createListTableColumns, ensure, Text, Link, Stack } from "sprint-common-web";

import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { RecordTypeBadge } from "../../../../common/components/RecordTypeBadge";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { NoteSubjectType } from "../../../../common/types/NoteSubjectType";
import { RecordType } from "../../../../common/types/RecordType";
import { renderNotes } from "../../../../common/utils/renderNotes";
import { LanguageAssetInfo } from "../../../common/components/listings/AssetsAvailabilityList";
import { VideoInfo } from "../../../common/components/listings/VideoInfo";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../../common/types/OrderAssetStorageType";
import { stringifyOrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import { renderNoteBadge } from "../../../common/utils/renderNoteBadge";
import {
    OnPremOrderAssetDashboardItem,
    OnPremOrderAssetSoundtrackDashboardItem,
    OnPremOrderAssetSubtitleDashboardItem,
    OnPremOrderAssetVideoDashboardItem
} from "./OnPremOrderAssetItem";

export const createOnPremOrderAssetsSectionColumns = ({
    areNotesLoading
}: {
    areNotesLoading?: boolean;
}): ListTableColumn<OnPremOrderAssetDashboardItem>[] => {
    return createListTableColumns<OnPremOrderAssetDashboardItem>([
        {
            type: "basic",
            key: "recordType",
            name: "Record type",
            minWidth: 50,
            maxWidth: 80,
            onRender: renderRecordType
        },
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 100,
            maxWidth: 150,
            onRender: renderName
        },
        {
            type: "basic",
            key: "availability",
            name: "Availability",
            minWidth: 80,
            maxWidth: 80,
            onRender: renderAvailability
        },
        {
            type: "basic",
            key: "filePath",
            name: "Path",
            minWidth: 40,
            maxWidth: 200,
            onRender: renderFilePath
        },
        {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 80,
            maxWidth: 200,
            onRender: renderDetails
        },
        {
            type: "basic",
            key: "notes",
            name: "Notes",
            minWidth: 120,
            maxWidth: 120,
            isResizable: false,
            onRender: (item) =>
                renderNotes({
                    subjectType: NoteSubjectType.OnPremAsset,
                    notes: item?.notes ?? [],
                    itemId: item?.assetId,
                    renderBadge: (noteItem: NoteItem) => renderNoteBadge(noteItem),
                    isLoading: areNotesLoading
                })
        }
    ]);
};

const createAssetLink = (item: OnPremOrderAssetDashboardItem, children: React.ReactNode): JSX.Element => {
    const href =
        item.assetHref && item.availability === OrderAssetAvailability.FullMatch ? "/#" + item.assetHref : undefined;

    return (
        <Link href={href} disabled={!href}>
            {children}
        </Link>
    );
};
const renderVideoName = (item: OnPremOrderAssetDashboardItem): JSX.Element =>
    createAssetLink(
        item,
        <Text tooltip={item.name} variant="smallPlus">
            {item.name}
        </Text>
    );

const renderLanguageName = (item: OnPremOrderAssetDashboardItem): JSX.Element => {
    if (item.recordType === RecordType.Video) return <></>;

    return createAssetLink(
        item,
        <Text
            tooltip={item.name}
            variant="smallPlus"
            key={item.assetId}
            value={getLanguageDisplayName(item.details.language)}
        />
    );
};

const renderName = (item?: OnPremOrderAssetDashboardItem): JSX.Element | null => {
    ensure(item);

    return item.recordType === RecordType.Video ? renderVideoName(item) : renderLanguageName(item);
};

const renderRecordType = (item?: OnPremOrderAssetDashboardItem): JSX.Element => {
    ensure(item);

    return <RecordTypeBadge value={item.recordType} />;
};

const renderFilePath = (item?: OnPremOrderAssetDashboardItem) => {
    ensure(item);

    return <Text variant="small" tooltip={item.filePath} value={item.filePath} />;
};

const renderVideoAvailability = (item: OnPremOrderAssetVideoDashboardItem): JSX.Element => {
    return (
        <VideoInfo
            availability={item.availability}
            requestedAspectRatio={item.details.aspectRatio}
            type={OrderAssetStorageType.OnPrem}
            nonMatchingVideos={item.details.nonMatchingVideos}
            isTrailer={item.isTrailer}
            requestedResolution={item.details.targetResolution}
            testId="availability-indicator"
        />
    );
};

const renderLanguageAvailability = (
    item: OnPremOrderAssetSubtitleDashboardItem | OnPremOrderAssetSoundtrackDashboardItem
): JSX.Element => {
    return (
        <LanguageAssetInfo
            assetType={item.recordType}
            item={{ availability: item.availability, language: item.details.language }}
            storageType={OrderAssetStorageType.OnPrem}
            isTrailer={item.isTrailer}
            testId="availability-indicator"
        />
    );
};

const renderAvailability = (item?: OnPremOrderAssetDashboardItem): JSX.Element | null => {
    ensure(item);

    switch (item.recordType) {
        case RecordType.Video:
            return renderVideoAvailability(item);
        case RecordType.Audio:
        case RecordType.Subtitle:
            return renderLanguageAvailability(item);
        default:
            return <></>;
    }
};

const renderVideoDetails = (item: OnPremOrderAssetVideoDashboardItem): JSX.Element => (
    <Stack>
        <Text variant="small">{getLanguageDisplayName(item.details.subtitle)}</Text>
        <Text variant="small">{item.details.aspectRatio.name}</Text>
        {item.details.sourceResolution && (
            <Text variant="small">{stringifyOrderResolutionDimensions(item.details.sourceResolution)}</Text>
        )}
    </Stack>
);

const renderDetails = (item?: OnPremOrderAssetDashboardItem): JSX.Element => {
    ensure(item);

    if (item.recordType !== RecordType.Video) {
        return <Text>-</Text>;
    }

    const videoItem = item;

    return renderVideoDetails(videoItem);
};
